import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Step {
  id: string;
  phase: string;
  title: string;
  location: string;
  duration: string;
  details: string[];
  code?: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  result?: any;
}

interface ActionLogEntry {
  id: string;
  timestamp: Date;
  action: string;
  type: 'create' | 'upload' | 'list' | 'delete';
  data: any;
  canUndo: boolean;
}

interface BusinessRoute {
  id: string;
  businessName: string;
  slug: string;
  createdAt: Date;
  uploadCount: number;
  totalSize: number;
  files: UploadedFile[];
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: Date;
  uploaderEmail: string;
  storageKey: string;
  dataUrl?: string;
  type?: string;
}

const BusinessFileSharingInteractive: React.FC = () => {
  const [activeProcess, setActiveProcess] = useState<'create' | 'upload' | 'list' | 'admin'>('create');
  const [runningProcess, setRunningProcess] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);

  // Form inputs
  const [businessName, setBusinessName] = useState('');
  const [slug, setSlug] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploaderEmail, setUploaderEmail] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Data storage
  const [businessRoutes, setBusinessRoutes] = useState<BusinessRoute[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<BusinessRoute | null>(null);
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([]);
  const [showActionLog, setShowActionLog] = useState(true);

  // Process steps
  const [createSteps, setCreateSteps] = useState<Step[]>([]);
  const [uploadSteps, setUploadSteps] = useState<Step[]>([]);
  const [listSteps, setListSteps] = useState<Step[]>([]);

  // Load saved data on mount
  useEffect(() => {
    const savedRoutes = localStorage.getItem('interactive_business_routes');
    const savedLog = localStorage.getItem('interactive_action_log');

    if (savedRoutes) {
      setBusinessRoutes(JSON.parse(savedRoutes).map((r: any) => ({
        ...r,
        createdAt: new Date(r.createdAt)
      })));
    }

    if (savedLog) {
      setActionLog(JSON.parse(savedLog).map((l: any) => ({
        ...l,
        timestamp: new Date(l.timestamp)
      })));
    }
  }, []);

  // Save data whenever it changes with error handling
  useEffect(() => {
    try {
      localStorage.setItem('interactive_business_routes', JSON.stringify(businessRoutes));
    } catch (e) {
      console.warn('Failed to save business routes to localStorage:', e);
    }
  }, [businessRoutes]);

  useEffect(() => {
    try {
      // Limit action log to last 50 entries to prevent quota exceeded
      const limitedLog = actionLog.slice(-50);
      localStorage.setItem('interactive_action_log', JSON.stringify(limitedLog));
    } catch (e) {
      console.warn('Failed to save action log to localStorage:', e);
      // Clear old entries if quota exceeded
      try {
        localStorage.removeItem('interactive_action_log');
        const minimalLog = actionLog.slice(-10);
        localStorage.setItem('interactive_action_log', JSON.stringify(minimalLog));
      } catch (e2) {
        console.error('Failed to save even minimal log:', e2);
      }
    }
  }, [actionLog]);

  // Generate slug from business name
  useEffect(() => {
    if (businessName) {
      const newSlug = businessName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setSlug(newSlug);
    } else {
      setSlug('');
    }
  }, [businessName]);

  // Add to action log
  const addToLog = (action: string, type: ActionLogEntry['type'], data: any, canUndo = true) => {
    const entry: ActionLogEntry = {
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      action,
      type,
      data,
      canUndo
    };
    setActionLog(prev => [entry, ...prev]);
  };

  // Initialize create steps
  const initCreateSteps = (): Step[] => [
    {
      id: 'validate',
      phase: 'Input Validation',
      title: 'Validate Business Name',
      location: 'Browser (JavaScript)',
      duration: '< 1ms',
      details: [
        'Check name length >= 3 characters',
        'Remove special characters',
        'Convert to URL-safe slug',
        'Check for reserved words'
      ],
      status: 'pending'
    },
    {
      id: 'check-availability',
      phase: 'Database Check',
      title: 'Check Slug Availability',
      location: 'LocalStorage (Demo)',
      duration: '5ms',
      details: [
        'Query existing routes',
        'Check for slug collision',
        'Return availability status'
      ],
      status: 'pending'
    },
    {
      id: 'create-route',
      phase: 'Route Creation',
      title: 'Create Business Route',
      location: 'LocalStorage (Demo)',
      duration: '10ms',
      details: [
        'Generate unique route ID',
        'Store business route record',
        'Set creation timestamp',
        'Initialize counters'
      ],
      status: 'pending'
    },
    {
      id: 'create-storage',
      phase: 'Storage Setup',
      title: 'Initialize Storage Space',
      location: 'LocalStorage (Demo)',
      duration: '5ms',
      details: [
        'Create storage namespace',
        'Set initial metadata',
        'Configure permissions',
        'Initialize file list'
      ],
      status: 'pending'
    },
    {
      id: 'generate-url',
      phase: 'URL Generation',
      title: 'Generate Shareable URL',
      location: 'Browser',
      duration: '< 1ms',
      details: [
        'Construct full URL',
        'Add to route registry',
        'Return to user'
      ],
      status: 'pending'
    }
  ];

  // Initialize upload steps
  const initUploadSteps = (): Step[] => [
    {
      id: 'verify-route',
      phase: 'Route Verification',
      title: 'Verify Business Route Exists',
      location: 'LocalStorage (Demo)',
      duration: '5ms',
      details: [
        'Look up route by slug',
        'Verify route is active',
        'Check upload permissions'
      ],
      status: 'pending'
    },
    {
      id: 'validate-file',
      phase: 'File Validation',
      title: 'Validate Upload File',
      location: 'Browser',
      duration: '< 5ms',
      details: [
        'Check file size',
        'Verify file type',
        'Scan filename'
      ],
      status: 'pending'
    },
    {
      id: 'generate-key',
      phase: 'Key Generation',
      title: 'Generate Storage Key',
      location: 'Browser',
      duration: '< 1ms',
      details: [
        'Generate unique file ID',
        'Create storage path',
        'Add timestamp'
      ],
      status: 'pending'
    },
    {
      id: 'store-file',
      phase: 'File Storage',
      title: 'Store File Metadata',
      location: 'LocalStorage (Demo)',
      duration: '10ms',
      details: [
        'Store file information',
        'Update route statistics',
        'Add to file list'
      ],
      status: 'pending'
    },
    {
      id: 'complete',
      phase: 'Completion',
      title: 'Upload Complete',
      location: 'Browser',
      duration: '< 1ms',
      details: [
        'Generate success response',
        'Update UI',
        'Log action'
      ],
      status: 'pending'
    }
  ];

  // Initialize list steps
  const initListSteps = (): Step[] => [
    {
      id: 'load-route',
      phase: 'Route Loading',
      title: 'Load Business Route',
      location: 'LocalStorage (Demo)',
      duration: '5ms',
      details: [
        'Query route by slug',
        'Load route metadata',
        'Get statistics'
      ],
      status: 'pending'
    },
    {
      id: 'query-files',
      phase: 'File Query',
      title: 'Query Uploaded Files',
      location: 'LocalStorage (Demo)',
      duration: '10ms',
      details: [
        'Get file list',
        'Sort by date',
        'Apply pagination'
      ],
      status: 'pending'
    },
    {
      id: 'format-data',
      phase: 'Data Formatting',
      title: 'Format File Data',
      location: 'Browser',
      duration: '< 5ms',
      details: [
        'Format file sizes',
        'Format dates',
        'Prepare display data'
      ],
      status: 'pending'
    },
    {
      id: 'render',
      phase: 'Rendering',
      title: 'Display File Gallery',
      location: 'Browser',
      duration: '50ms',
      details: [
        'Render file cards',
        'Show statistics',
        'Display actions'
      ],
      status: 'pending'
    }
  ];

  // Run create process
  const runCreateProcess = async () => {
    if (!businessName || businessName.length < 3) {
      alert('Please enter a business name (at least 3 characters)');
      return;
    }

    setRunningProcess(true);
    const steps = initCreateSteps();
    setCreateSteps(steps);
    setCurrentStepIndex(0);

    // Step 1: Validate
    steps[0].status = 'active';
    setCreateSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 200));

    if (slug.length < 3) {
      steps[0].status = 'error';
      steps[0].result = 'Name too short';
      setCreateSteps([...steps]);
      setRunningProcess(false);
      return;
    }

    steps[0].status = 'completed';
    steps[0].result = `Slug: ${slug}`;
    setCreateSteps([...steps]);

    // Step 2: Check availability
    setCurrentStepIndex(1);
    steps[1].status = 'active';
    setCreateSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 300));

    const exists = businessRoutes.some(r => r.slug === slug);
    if (exists) {
      steps[1].status = 'error';
      steps[1].result = 'Slug already exists';
      setCreateSteps([...steps]);
      setRunningProcess(false);
      return;
    }

    steps[1].status = 'completed';
    steps[1].result = 'Available!';
    setCreateSteps([...steps]);

    // Step 3: Create route
    setCurrentStepIndex(2);
    steps[2].status = 'active';
    setCreateSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 400));

    const newRoute: BusinessRoute = {
      id: Math.random().toString(36).substr(2, 9),
      businessName,
      slug,
      createdAt: new Date(),
      uploadCount: 0,
      totalSize: 0,
      files: []
    };

    steps[2].status = 'completed';
    steps[2].result = `Route ID: ${newRoute.id}`;
    setCreateSteps([...steps]);

    // Step 4: Create storage
    setCurrentStepIndex(3);
    steps[3].status = 'active';
    setCreateSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 300));

    steps[3].status = 'completed';
    steps[3].result = `Storage: /business/${slug}/`;
    setCreateSteps([...steps]);

    // Step 5: Generate URL
    setCurrentStepIndex(4);
    steps[4].status = 'active';
    setCreateSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 200));

    const url = `${window.location.origin}/files/${slug}`;
    steps[4].status = 'completed';
    steps[4].result = url;
    setCreateSteps([...steps]);

    // Save the route
    setBusinessRoutes(prev => [...prev, newRoute]);
    setSelectedRoute(newRoute);

    // Add to action log
    addToLog(
      `Created business route: ${businessName} (${slug})`,
      'create',
      { route: newRoute, url },
      true
    );

    // Copy URL to clipboard
    navigator.clipboard.writeText(url);

    setRunningProcess(false);
    setCurrentStepIndex(-1);

    // Clear form
    setTimeout(() => {
      setBusinessName('');
      setSlug('');
    }, 2000);
  };

  // Run upload process
  const runUploadProcess = async () => {
    if (!selectedRoute) {
      alert('Please select a business route first');
      return;
    }
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }
    if (!uploaderEmail) {
      alert('Please enter your email');
      return;
    }

    setRunningProcess(true);
    const steps = initUploadSteps();
    setUploadSteps(steps);
    setCurrentStepIndex(0);

    // Step 1: Verify route
    steps[0].status = 'active';
    setUploadSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 300));

    steps[0].status = 'completed';
    steps[0].result = `Route: ${selectedRoute.slug}`;
    setUploadSteps([...steps]);

    // Step 2: Validate file
    setCurrentStepIndex(1);
    steps[1].status = 'active';
    setUploadSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 200));

    if (selectedFile.size > 100 * 1024 * 1024) {
      steps[1].status = 'error';
      steps[1].result = 'File too large (>100MB)';
      setUploadSteps([...steps]);
      setRunningProcess(false);
      return;
    }

    steps[1].status = 'completed';
    steps[1].result = `${(selectedFile.size / 1024).toFixed(1)}KB`;
    setUploadSteps([...steps]);

    // Step 3: Generate key
    setCurrentStepIndex(2);
    steps[2].status = 'active';
    setUploadSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 100));

    const fileId = Math.random().toString(36).substr(2, 9);
    const storageKey = `business/${selectedRoute.slug}/${Date.now()}_${fileId}_${selectedFile.name}`;

    steps[2].status = 'completed';
    steps[2].result = `Key: .../${fileId}_${selectedFile.name}`;
    setUploadSteps([...steps]);

    // Step 4: Store file
    setCurrentStepIndex(3);
    steps[3].status = 'active';
    setUploadSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 400));

    // Don't store actual file content in localStorage to avoid quota issues
    // In production, this would be uploaded to R2/S3
    const newFile: UploadedFile = {
      id: fileId,
      name: selectedFile.name,
      size: selectedFile.size,
      uploadedAt: new Date(),
      uploaderEmail,
      storageKey,
      dataUrl: '', // Don't store actual data to avoid quota exceeded
      type: selectedFile.type
    };

    // Update route with new file
    const updatedRoutes = businessRoutes.map(r => {
      if (r.id === selectedRoute.id) {
        return {
          ...r,
          uploadCount: r.uploadCount + 1,
          totalSize: r.totalSize + selectedFile.size,
          files: [...r.files, newFile]
        };
      }
      return r;
    });

    setBusinessRoutes(updatedRoutes);
    setSelectedRoute(updatedRoutes.find(r => r.id === selectedRoute.id)!);

    steps[3].status = 'completed';
    steps[3].result = 'Stored successfully';
    setUploadSteps([...steps]);

    // Step 5: Complete
    setCurrentStepIndex(4);
    steps[4].status = 'active';
    setUploadSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 200));

    steps[4].status = 'completed';
    steps[4].result = 'Upload complete!';
    setUploadSteps([...steps]);

    // Add to action log
    addToLog(
      `Uploaded file: ${selectedFile.name} to ${selectedRoute.businessName}`,
      'upload',
      { file: newFile, route: selectedRoute.slug },
      true
    );

    setRunningProcess(false);
    setCurrentStepIndex(-1);

    // Clear form
    setTimeout(() => {
      setSelectedFile(null);
      setUploaderEmail('');
    }, 2000);
  };

  // Run list process
  const runListProcess = async () => {
    if (!selectedRoute) {
      alert('Please select a business route first');
      return;
    }

    setRunningProcess(true);
    const steps = initListSteps();
    setListSteps(steps);
    setCurrentStepIndex(0);

    // Step 1: Load route
    steps[0].status = 'active';
    setListSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 300));

    steps[0].status = 'completed';
    steps[0].result = `Loaded: ${selectedRoute.businessName}`;
    setListSteps([...steps]);

    // Step 2: Query files
    setCurrentStepIndex(1);
    steps[1].status = 'active';
    setListSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 400));

    steps[1].status = 'completed';
    steps[1].result = `Found ${selectedRoute.files.length} files`;
    setListSteps([...steps]);

    // Step 3: Format data
    setCurrentStepIndex(2);
    steps[2].status = 'active';
    setListSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 200));

    steps[2].status = 'completed';
    steps[2].result = 'Formatted for display';
    setListSteps([...steps]);

    // Step 4: Render
    setCurrentStepIndex(3);
    steps[3].status = 'active';
    setListSteps([...steps]);
    await new Promise(resolve => setTimeout(resolve, 500));

    steps[3].status = 'completed';
    steps[3].result = 'Gallery rendered';
    setListSteps([...steps]);

    // Add to action log
    addToLog(
      `Listed files for: ${selectedRoute.businessName}`,
      'list',
      { route: selectedRoute.slug, fileCount: selectedRoute.files.length },
      false
    );

    setRunningProcess(false);
    setCurrentStepIndex(-1);
  };

  // Undo action
  const undoAction = (entry: ActionLogEntry) => {
    if (!entry.canUndo) return;

    if (entry.type === 'create') {
      // Remove the created route
      const routeId = entry.data.route.id;
      setBusinessRoutes(prev => prev.filter(r => r.id !== routeId));
      if (selectedRoute?.id === routeId) {
        setSelectedRoute(null);
      }
      addToLog(
        `Removed route: ${entry.data.route.businessName}`,
        'delete',
        { routeId },
        false
      );
    } else if (entry.type === 'upload') {
      // Remove the uploaded file
      const fileId = entry.data.file.id;
      const routeSlug = entry.data.route;

      const updatedRoutes = businessRoutes.map(r => {
        if (r.slug === routeSlug) {
          const fileToRemove = r.files.find(f => f.id === fileId);
          return {
            ...r,
            uploadCount: r.uploadCount - 1,
            totalSize: r.totalSize - (fileToRemove?.size || 0),
            files: r.files.filter(f => f.id !== fileId)
          };
        }
        return r;
      });

      setBusinessRoutes(updatedRoutes);
      if (selectedRoute?.slug === routeSlug) {
        setSelectedRoute(updatedRoutes.find(r => r.slug === routeSlug)!);
      }

      addToLog(
        `Removed file: ${entry.data.file.name}`,
        'delete',
        { fileId },
        false
      );
    }
  };

  // Clear all data
  const clearAllData = () => {
    if (!confirm('This will remove all business routes and files. Are you sure?')) return;

    setBusinessRoutes([]);
    setSelectedRoute(null);
    setActionLog([]);
    localStorage.removeItem('interactive_business_routes');
    localStorage.removeItem('interactive_action_log');
  };

  // Download file
  const downloadFile = (file: UploadedFile) => {
    if (!file.dataUrl) {
      alert('File data not available for download');
      return;
    }

    const link = document.createElement('a');
    link.href = file.dataUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    addToLog(
      `Downloaded file: ${file.name}`,
      'list',
      { fileName: file.name },
      false
    );
  };

  // Send URL via email (simulated)
  const sendUrlViaEmail = async (route: BusinessRoute) => {
    if (!clientEmail) {
      alert('Please enter client email');
      return;
    }

    const url = `${window.location.origin}/files/${route.slug}`;

    // Simulate sending email
    setEmailSent(true);

    // Copy email content to clipboard
    const emailContent = `
Subject: File Upload Link for ${route.businessName}

Dear Client,

You can upload files for ${route.businessName} using this link:
${url}

This link allows you to securely upload files directly to our system.

Best regards,
${route.businessName}
    `;

    navigator.clipboard.writeText(emailContent);

    addToLog(
      `Sent upload link to: ${clientEmail}`,
      'create',
      { email: clientEmail, url, businessName: route.businessName },
      false
    );

    // Show success message
    setTimeout(() => {
      alert(`Email template copied to clipboard!\n\nSend to: ${clientEmail}\nLink: ${url}`);
      setEmailSent(false);
      setClientEmail('');
    }, 500);
  };

  // Get current steps based on active process
  const getCurrentSteps = () => {
    switch (activeProcess) {
      case 'create': return createSteps;
      case 'upload': return uploadSteps;
      case 'list': return listSteps;
      default: return [];
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            🚀 Interactive Business File Sharing
          </h1>
          <p className="text-xl text-gray-300">
            Actually create routes, upload files, and manage everything
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Controls */}
          <div className="lg:col-span-2 space-y-6">
            {/* Process Tabs */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveProcess('create')}
                className={`px-6 py-3 rounded-lg font-semibold transition ${
                  activeProcess === 'create'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                📁 Create Route
              </button>
              <button
                onClick={() => setActiveProcess('upload')}
                className={`px-6 py-3 rounded-lg font-semibold transition ${
                  activeProcess === 'upload'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                📤 Upload File
              </button>
              <button
                onClick={() => setActiveProcess('list')}
                className={`px-6 py-3 rounded-lg font-semibold transition ${
                  activeProcess === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                📋 List Files
              </button>
              <button
                onClick={() => setActiveProcess('admin')}
                className={`px-6 py-3 rounded-lg font-semibold transition ${
                  activeProcess === 'admin'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                👤 Admin
              </button>
            </div>

            {/* Process Controls */}
            <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6">
              {activeProcess === 'create' && (
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold mb-4">Create Business Route</h2>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Business Name</label>
                    <input
                      type="text"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="e.g., Acme Corporation"
                      className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  {slug && (
                    <div className="bg-gray-900 rounded-lg p-3">
                      <span className="text-sm text-gray-400">URL will be: </span>
                      <span className="text-blue-400 font-mono">/files/{slug}</span>
                    </div>
                  )}
                  <button
                    onClick={runCreateProcess}
                    disabled={runningProcess || !businessName}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {runningProcess ? 'Creating...' : 'Create Business Route'}
                  </button>
                </div>
              )}

              {activeProcess === 'upload' && (
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold mb-4">Upload File</h2>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Select Business</label>
                    <select
                      value={selectedRoute?.id || ''}
                      onChange={(e) => setSelectedRoute(businessRoutes.find(r => r.id === e.target.value) || null)}
                      className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">Select a business...</option>
                      {businessRoutes.map(route => (
                        <option key={route.id} value={route.id}>
                          {route.businessName} ({route.uploadCount} files)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Select File</label>
                    <input
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Your Email</label>
                    <input
                      type="email"
                      value={uploaderEmail}
                      onChange={(e) => setUploaderEmail(e.target.value)}
                      placeholder="john@example.com"
                      className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <button
                    onClick={runUploadProcess}
                    disabled={runningProcess || !selectedRoute || !selectedFile || !uploaderEmail}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {runningProcess ? 'Uploading...' : 'Upload File'}
                  </button>
                </div>
              )}

              {activeProcess === 'list' && (
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold mb-4">List Files</h2>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Select Business</label>
                    <select
                      value={selectedRoute?.id || ''}
                      onChange={(e) => setSelectedRoute(businessRoutes.find(r => r.id === e.target.value) || null)}
                      className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">Select a business...</option>
                      {businessRoutes.map(route => (
                        <option key={route.id} value={route.id}>
                          {route.businessName} ({route.uploadCount} files)
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedRoute && (
                    <div className="bg-gray-900 rounded-lg p-4">
                      <div className="text-sm text-gray-400 mb-2">Statistics:</div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-2xl font-bold">{selectedRoute.uploadCount}</div>
                          <div className="text-xs text-gray-500">Total Files</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">
                            {(selectedRoute.totalSize / 1024 / 1024).toFixed(2)} MB
                          </div>
                          <div className="text-xs text-gray-500">Total Size</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={runListProcess}
                    disabled={runningProcess || !selectedRoute}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {runningProcess ? 'Loading...' : 'List Files'}
                  </button>
                </div>
              )}

              {activeProcess === 'admin' && (
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold mb-4">👤 Admin Dashboard</h2>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-900 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-blue-400">
                        {businessRoutes.length}
                      </div>
                      <div className="text-sm text-gray-400">Total Businesses</div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-400">
                        {businessRoutes.reduce((sum, r) => sum + r.uploadCount, 0)}
                      </div>
                      <div className="text-sm text-gray-400">Total Files</div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-purple-400">
                        {(businessRoutes.reduce((sum, r) => sum + r.totalSize, 0) / 1024 / 1024).toFixed(2)} MB
                      </div>
                      <div className="text-sm text-gray-400">Total Storage</div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-yellow-400">
                        {actionLog.length}
                      </div>
                      <div className="text-sm text-gray-400">Total Actions</div>
                    </div>
                  </div>

                  {/* All Businesses Table */}
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h3 className="text-xl font-bold mb-4">🏢 All Business Routes</h3>

                    {businessRoutes.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No businesses created yet</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left">
                          <thead className="border-b border-gray-700">
                            <tr>
                              <th className="pb-3 text-gray-400 font-medium">Business Name</th>
                              <th className="pb-3 text-gray-400 font-medium">URL Slug</th>
                              <th className="pb-3 text-gray-400 font-medium text-center">Files</th>
                              <th className="pb-3 text-gray-400 font-medium text-center">Storage</th>
                              <th className="pb-3 text-gray-400 font-medium">Created</th>
                              <th className="pb-3 text-gray-400 font-medium text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-800">
                            {businessRoutes.map(route => (
                              <tr key={route.id} className="hover:bg-gray-800/50 transition">
                                <td className="py-3">
                                  <div className="font-semibold">{route.businessName}</div>
                                  <div className="text-xs text-gray-500">ID: {route.id}</div>
                                </td>
                                <td className="py-3">
                                  <code className="text-sm text-blue-400">/{route.slug}</code>
                                </td>
                                <td className="py-3 text-center">
                                  <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-sm font-semibold">
                                    {route.uploadCount}
                                  </span>
                                </td>
                                <td className="py-3 text-center">
                                  <span className="text-sm">
                                    {(route.totalSize / 1024 / 1024).toFixed(2)} MB
                                  </span>
                                </td>
                                <td className="py-3">
                                  <div className="text-sm text-gray-400">
                                    {new Date(route.createdAt).toLocaleDateString()}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {new Date(route.createdAt).toLocaleTimeString()}
                                  </div>
                                </td>
                                <td className="py-3">
                                  <div className="flex gap-2 justify-center">
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(`${window.location.origin}/files/${route.slug}`);
                                        alert('URL copied!');
                                      }}
                                      className="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs font-semibold transition"
                                      title="Copy URL"
                                    >
                                      📋
                                    </button>
                                    <button
                                      onClick={() => {
                                        setSelectedRoute(route);
                                        setActiveProcess('list');
                                      }}
                                      className="px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-xs font-semibold transition"
                                      title="View Files"
                                    >
                                      👁️
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (confirm(`Delete ${route.businessName} and all its files?`)) {
                                          setBusinessRoutes(prev => prev.filter(r => r.id !== route.id));
                                          if (selectedRoute?.id === route.id) {
                                            setSelectedRoute(null);
                                          }
                                          addToLog(
                                            `Admin deleted: ${route.businessName}`,
                                            'delete',
                                            { route: route.slug },
                                            false
                                          );
                                        }
                                      }}
                                      className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs font-semibold transition"
                                      title="Delete"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Recent Activity */}
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h3 className="text-xl font-bold mb-4">📊 Recent Activity (Last 10)</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {actionLog.slice(0, 10).map((entry, index) => (
                        <div key={entry.id} className="flex justify-between items-center bg-gray-800 rounded p-2">
                          <div className="flex-1">
                            <div className="text-sm">{entry.action}</div>
                            <div className="text-xs text-gray-500">
                              {entry.timestamp.toLocaleString()}
                            </div>
                          </div>
                          <div className={`px-2 py-1 rounded text-xs font-semibold ${
                            entry.type === 'create' ? 'bg-blue-500/20 text-blue-400' :
                            entry.type === 'upload' ? 'bg-green-500/20 text-green-400' :
                            entry.type === 'list' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {entry.type}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bulk Actions */}
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h3 className="text-xl font-bold mb-4">⚡ Bulk Actions</h3>
                    <div className="flex gap-4">
                      <button
                        onClick={() => {
                          const csv = [
                            ['Business Name', 'URL Slug', 'Files', 'Storage (MB)', 'Created'],
                            ...businessRoutes.map(r => [
                              r.businessName,
                              r.slug,
                              r.uploadCount,
                              (r.totalSize / 1024 / 1024).toFixed(2),
                              new Date(r.createdAt).toISOString()
                            ])
                          ].map(row => row.join(',')).join('\n');

                          const blob = new Blob([csv], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'businesses.csv';
                          a.click();

                          addToLog('Exported businesses to CSV', 'list', {}, false);
                        }}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded font-semibold transition"
                      >
                        📥 Export CSV
                      </button>
                      <button
                        onClick={() => {
                          const urls = businessRoutes.map(r =>
                            `${r.businessName}: ${window.location.origin}/files/${r.slug}`
                          ).join('\n');
                          navigator.clipboard.writeText(urls);
                          alert('All URLs copied to clipboard!');
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded font-semibold transition"
                      >
                        📋 Copy All URLs
                      </button>
                      <button
                        onClick={clearAllData}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded font-semibold transition"
                      >
                        🗑️ Clear Everything
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Steps Visualization */}
            {getCurrentSteps().length > 0 && (
              <div className="space-y-3">
                {getCurrentSteps().map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-gray-800/50 rounded-lg p-4 border-2 transition-all ${
                      step.status === 'active' ? 'border-blue-500 shadow-lg shadow-blue-500/20' :
                      step.status === 'completed' ? 'border-green-500' :
                      step.status === 'error' ? 'border-red-500' :
                      'border-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        step.status === 'completed' ? 'bg-green-500' :
                        step.status === 'active' ? 'bg-blue-500 animate-pulse' :
                        step.status === 'error' ? 'bg-red-500' :
                        'bg-gray-700'
                      }`}>
                        {step.status === 'completed' ? '✓' :
                         step.status === 'error' ? '✗' :
                         index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{step.title}</div>
                        <div className="text-sm text-gray-400">{step.location} • {step.duration}</div>
                        {step.result && (
                          <div className="text-sm text-blue-400 mt-1">→ {step.result}</div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* File Gallery (for List process) */}
            {activeProcess === 'list' && selectedRoute && selectedRoute.files.length > 0 && (
              <div className="bg-gray-800/50 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4">📁 Uploaded Files</h3>
                <div className="space-y-3">
                  {selectedRoute.files.map(file => (
                    <div key={file.id} className="bg-gray-900 rounded-lg p-4 hover:bg-gray-850 transition">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">
                              {file.type?.includes('image') ? '🖼️' :
                               file.type?.includes('pdf') ? '📄' :
                               file.type?.includes('video') ? '🎥' : '📎'}
                            </span>
                            <div className="font-medium text-lg">{file.name}</div>
                          </div>
                          <div className="text-sm text-gray-400 mt-1">
                            {(file.size / 1024).toFixed(1)} KB • {new Date(file.uploadedAt).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">From: {file.uploaderEmail}</div>
                        </div>
                        <button
                          onClick={() => downloadFile(file)}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition flex items-center gap-2"
                        >
                          ⬇️ Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Send URL to Client */}
                <div className="mt-6 p-4 bg-gray-900 rounded-lg">
                  <h4 className="font-bold mb-3">📧 Send Upload Link to Client</h4>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="client@example.com"
                      className="flex-1 px-3 py-2 bg-gray-800 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                    />
                    <button
                      onClick={() => sendUrlViaEmail(selectedRoute)}
                      disabled={!clientEmail || emailSent}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold disabled:opacity-50 transition"
                    >
                      {emailSent ? 'Sending...' : 'Send Link'}
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    URL: {window.location.origin}/files/{selectedRoute.slug}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Action Log */}
          <div className="space-y-4">
            <div className="bg-gray-800/50 backdrop-blur rounded-xl p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">📜 Action Log</h3>
                <button
                  onClick={() => setShowActionLog(!showActionLog)}
                  className="text-sm text-gray-400 hover:text-white"
                >
                  {showActionLog ? 'Hide' : 'Show'}
                </button>
              </div>

              <AnimatePresence>
                {showActionLog && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="space-y-2"
                  >
                    {actionLog.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No actions yet</p>
                    ) : (
                      <>
                        <div className="max-h-96 overflow-y-auto space-y-2">
                          {actionLog.map(entry => (
                            <motion.div
                              key={entry.id}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              className={`bg-gray-900 rounded-lg p-3 text-sm ${
                                entry.type === 'delete' ? 'border border-red-500/30' : ''
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="font-medium">{entry.action}</div>
                                  <div className="text-xs text-gray-500">
                                    {entry.timestamp.toLocaleTimeString()}
                                  </div>
                                </div>
                                {entry.canUndo && (
                                  <button
                                    onClick={() => undoAction(entry)}
                                    className="text-xs text-red-400 hover:text-red-300 ml-2"
                                  >
                                    Undo
                                  </button>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                        <div className="pt-2 border-t border-gray-700">
                          <button
                            onClick={clearAllData}
                            className="w-full py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold transition"
                          >
                            🗑️ Clear All Data
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Quick Stats */}
            <div className="bg-gray-800/50 rounded-xl p-4">
              <h3 className="text-lg font-bold mb-3">📊 Quick Stats</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Routes:</span>
                  <span className="font-bold">{businessRoutes.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Files:</span>
                  <span className="font-bold">
                    {businessRoutes.reduce((sum, r) => sum + r.uploadCount, 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Size:</span>
                  <span className="font-bold">
                    {(businessRoutes.reduce((sum, r) => sum + r.totalSize, 0) / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
              </div>
            </div>

            {/* Existing Routes */}
            {businessRoutes.length > 0 && (
              <div className="bg-gray-800/50 rounded-xl p-4">
                <h3 className="text-lg font-bold mb-3">🏢 Business Routes</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {businessRoutes.map(route => (
                    <div
                      key={route.id}
                      className={`bg-gray-900 rounded-lg p-3 cursor-pointer transition ${
                        selectedRoute?.id === route.id ? 'ring-2 ring-blue-500' : ''
                      }`}
                      onClick={() => setSelectedRoute(route)}
                    >
                      <div className="font-medium">{route.businessName}</div>
                      <div className="text-xs text-gray-400">/{route.slug}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {route.uploadCount} files • {(route.totalSize / 1024).toFixed(1)} KB
                      </div>
                      <div className="mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(`${window.location.origin}/files/${route.slug}`);
                            alert('URL copied to clipboard!');
                          }}
                          className="text-xs bg-gray-800 hover:bg-gray-700 px-2 py-1 rounded transition"
                        >
                          📋 Copy URL
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessFileSharingInteractive;